import { create } from "zustand";
import { Reservation, StoreReservationSettings } from "@/models/storeModel";
import { useStoreManagementStore } from "./storeManagementStore";
import {
  fetchReservations,
  fetchReservationSettings,
  createReservation,
  updateReservation,
  deleteReservation,
  createReservationSettings,
} from "@/services/reservations/reservationService";
import { toast } from "@/hooks/use-toast";
import {
  sendReservationConfirmation,
  sendReservationToStore,
} from "@/services/notification/notificationService";
import { supabase } from "@/integrations/supabase/client";
import { pushNotificationService } from "@/services/notifications/pushNotificationService";

interface StoreReservationState {
  reservations: Reservation[];
  settings: StoreReservationSettings | null;
  isLoading: boolean;
  error: string | null;
  selectedReservation: Reservation | null;
  // Actions
  loadStoreReservationData: () => Promise<void>;
  createReservation: (
    reservation: Omit<Reservation, "id" | "created_at" | "updated_at">
  ) => Promise<Reservation | null>;
  updateReservation: (
    id: string,
    updates: Partial<Reservation>
  ) => Promise<Reservation | null>;
  deleteReservation: (id: string) => Promise<boolean>;
  updateSettings: (
    settings: Partial<StoreReservationSettings>
  ) => Promise<StoreReservationSettings | null>;
  reset: () => void;
  setSelectedReservation: (reservation: Reservation | null) => void;
  setupRealtimeReservations: (storeId: string) => () => void;
}

export const useStoreReservationStore = create<StoreReservationState>(
  (set, get) => ({
    reservations: [],
    settings: null,
    isLoading: false,
    error: null,
    selectedReservation: null,

    loadStoreReservationData: async () => {
      const selectedStoreId =
        useStoreManagementStore.getState().selectedStoreId;
      if (!selectedStoreId) {
        set({
          isLoading: false,
          error: "No store selected",
          reservations: [],
          settings: null,
        });
        return;
      }

      set({ isLoading: true, error: null });

      try {
        const [reservationsData, settingsData] = await Promise.all([
          fetchReservations(selectedStoreId),
          fetchReservationSettings(selectedStoreId),
        ]);

        set({
          reservations: reservationsData,
          settings: settingsData,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        console.error("Error loading store reservation data:", error);
        set({
          error: "Failed to load reservation data",
          isLoading: false,
          reservations: [],
          settings: null,
        });

        // Show toast only for unexpected errors
        toast({
          title: "Error",
          description: "Failed to load reservation data",
          variant: "destructive",
        });
      }
    },

    setSelectedReservation: (reservation: Reservation | null) => {
      set({ selectedReservation: reservation });
    },

    // Realtime subscription for reservations
    setupRealtimeReservations: (storeId: string) => {
      const channel = supabase
        .channel("reservations-changes")
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "reservations",
            filter: `store_id=eq.${storeId}`,
          },
          () => {
            get().loadStoreReservationData();
          }
        )
        .subscribe();
      // Return cleanup function
      return () => {
        supabase.removeChannel(channel);
      };
    },

    createReservation: async (reservationData) => {
      const selectedStoreId =
        useStoreManagementStore.getState().selectedStoreId;
      const store = useStoreManagementStore.getState().selectedStore;
      if (!selectedStoreId || !store) {
        toast({
          title: "Error",
          description: "No store selected",
          variant: "destructive",
        });
        return null;
      }

      try {
        const newReservation = await createReservation({
          ...reservationData,
        });
        if (newReservation) {
          set((state) => ({
            reservations: [...state.reservations, newReservation],
          }));

          // Send notifications (but don't fail the reservation if notifications fail)
          try {
            const confirmationData = {
              customer_name: reservationData.customer_name,
              customer_email: reservationData.customer_email,
              store_name: store.name,
              store_id: selectedStoreId,
              reservation_date: reservationData.reservation_date,
              reservation_time: reservationData.reservation_time,
              party_size: reservationData.party_size,
              table_name: reservationData.table_id || "Any Table",
              booking_url: `${window.location.origin}/booking/${store.id}`,
            };

            await sendReservationConfirmation(confirmationData);

            const storeNotificationData = {
              ...confirmationData,
              store_email: store.email,
              special_requests: reservationData.special_requests || "",
              dashboard_url: `${window.location.origin}/reservations`,
            };

            await sendReservationToStore(storeNotificationData);
            
            // Send push notification
            try {
              await pushNotificationService.notifyReservationCreated(newReservation);
            } catch (error) {
              console.error('Error sending push notification for reservation:', error);
            }
          } catch (notificationError) {
            console.error("Failed to send notifications:", notificationError);
            // Don't fail the reservation creation if notifications fail
          }

          return newReservation;
        }

        return newReservation;
      } catch (error) {
        console.error("Error creating reservation:", error);
        toast({
          title: "Error",
          description: "Failed to create reservation",
          variant: "destructive",
        });
        return null;
      }
    },

    updateReservation: async (id, updates) => {
      try {
        const updatedReservation = await updateReservation(id, updates);
        if (!updatedReservation) {
          toast({
            title: "Error",
            description: "Failed to update reservation",
            variant: "destructive",
          });
          return null;
        }
        if (updatedReservation) {
          set((state) => ({
            reservations: state.reservations.map((res) =>
              res.id === id ? updatedReservation : res
            ),
          }));
          toast({
            title: "Success",
            description: "Reservation updated successfully",
          });
        }

        return updatedReservation;
      } catch (error) {
        console.error("Error updating reservation:", error);
        toast({
          title: "Error",
          description: "Failed to update reservation",
          variant: "destructive",
        });
        return null;
      }
    },

    deleteReservation: async (id) => {
      try {
        const success = await deleteReservation(id);

        if (success) {
          set((state) => ({
            reservations: state.reservations.filter((res) => res.id !== id),
          }));
          toast({
            title: "Success",
            description: "Reservation deleted successfully",
          });
        }

        return success;
      } catch (error) {
        console.error("Error deleting reservation:", error);
        toast({
          title: "Error",
          description: "Failed to delete reservation",
          variant: "destructive",
        });
        return false;
      }
    },

    updateSettings: async (settingsData) => {
      const selectedStoreId =
        useStoreManagementStore.getState().selectedStoreId;
      if (!selectedStoreId) return null;

      try {
        const updatedSettings = await createReservationSettings(
          selectedStoreId,
          settingsData
        );

        if (updatedSettings) {
          set({ settings: updatedSettings });
          toast({
            title: "Success",
            description: "Reservation settings updated successfully",
          });
        }

        return updatedSettings;
      } catch (error) {
        console.error("Error updating settings:", error);
        toast({
          title: "Error",
          description: "Failed to update reservation settings",
          variant: "destructive",
        });
        return null;
      }
    },

    reset: () => {
      set({
        reservations: [],
        settings: null,
        isLoading: false,
        error: null,
      });
    },
  })
);

// Subscribe to store selection changes
useStoreManagementStore.subscribe((state) => {
  const selectedStoreId = state.selectedStoreId;
  if (selectedStoreId) {
    useStoreReservationStore.getState().loadStoreReservationData();
  } else {
    useStoreReservationStore.getState().reset();
  }
});
